/**
 * The receiver's side of a transfer (owner, 13 Oct 2026: "once surbhi
 * approves the request the party who will get the transfer will also get
 * approval request he will tick i accept with all accesorries then the
 * transfer will be done and they can sell the vehicle").
 *
 * Two faces of one list, as with the deliveries on their way: on the counter's
 * home, a card per approved transfer with what the vehicle carries, the
 * challan, the tick and Accept or Refuse; everywhere else, a one-line banner
 * that takes them home. Both poll once a minute and show nothing when nothing
 * waits.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, ArrowRight, FileDown } from 'lucide-react';
import { TRANSFER_ACCEPT_WORDS, type VehicleTransferRow } from '@bright/shared';

import { api, errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { Button, Checkbox, Mono, Plane, Textarea, useToast } from '@/components/ui';
import { placeOf, transferDay, transferKeys, useIncomingTransfers, useSaveChallan, type CounterBook } from './transfers-api';

function IncomingCard({ row, book }: { row: VehicleTransferRow; book: CounterBook }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const challan = useSaveChallan();
  const [ticked, setTicked] = useState(false);
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState('');
  const options = { book: book === 'tvs' ? ('tvs' as const) : null };
  const settled = () => {
    void queryClient.invalidateQueries({ queryKey: transferKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['portal-stock'] });
    void queryClient.invalidateQueries({ queryKey: ['my-sales'] });
  };
  const acceptIt = useMutation({
    mutationFn: () => api.post<VehicleTransferRow>(`/transfers/${row.id}/accept`, { withAllAccessories: true }, options),
    onSuccess: (done) => {
      toast.success('Transfer done', `${done.vehicle.chassisNo} is in your stock — it is yours to sell.`);
      settled();
    },
    onError: (cause) => {
      toast.error('The transfer was not accepted', errorMessage(cause));
      settled();
    },
  });
  const refuseIt = useMutation({
    mutationFn: () => api.post<VehicleTransferRow>(`/transfers/${row.id}/refuse`, { reason: reason.trim() }, options),
    onSuccess: () => {
      toast.success('Transfer refused', `${row.from.name} is told why.`);
      settled();
    },
    onError: (cause) => toast.error('That did not go through', errorMessage(cause)),
  });
  const busy = acceptIt.isPending || refuseIt.isPending;
  const what = [row.vehicle.model, row.vehicle.variant, row.vehicle.color].filter(Boolean).join(' · ');

  return (
    <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-body font-medium text-ink">{what || 'Vehicle'}</p>
          <p className="text-caption text-muted">
            <Mono value={row.vehicle.chassisNo} size="sm" /> · from {row.from.name}
            {placeOf(row.from) ? `, ${placeOf(row.from)}` : ''}
          </p>
          <p className="text-caption text-muted">
            Challan <Mono value={row.challanNo} size="sm" subdued />
            {row.approval ? ` · approved by ${row.approval.by ?? 'the office'} on ${transferDay(row.approval.at)}` : ''}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<FileDown className="h-4 w-4" aria-hidden />}
          loading={challan.savingId === row.id}
          onClick={() => void challan.save(row)}
        >
          Challan
        </Button>
      </div>

      {row.vehicle.spec.length > 0 ? (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 rounded-md bg-surface-2 px-3 py-2 sm:grid-cols-2">
          {row.vehicle.spec.map((line) => (
            <div key={line.label} className="flex min-w-0 gap-2 text-caption">
              <dt className="shrink-0 text-muted">{line.label}</dt>
              <dd className="min-w-0 truncate text-ink" title={line.value}>{line.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="rounded-md bg-surface-2 px-3 py-2 text-caption text-muted">
          The dispatch form recorded no accessories for this vehicle. Check it in person.
        </p>
      )}
      {row.note ? <p className="text-caption text-ink-2">Note from {row.from.name}: {row.note}</p> : null}
      {row.stale ? (
        <p className="text-caption text-age-warn">This vehicle is no longer with {row.from.name}. The transfer will be void.</p>
      ) : null}

      {refusing ? (
        <div className="flex flex-col gap-2">
          <Textarea
            label="Why are you not taking it?"
            rows={2}
            maxLength={300}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="A few words — the sender and the office read it"
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setRefusing(false)}>
              Back
            </Button>
            <Button variant="danger" size="sm" loading={refuseIt.isPending} disabled={busy || reason.trim().length < 5} onClick={() => refuseIt.mutate()}>
              Refuse transfer
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Checkbox label={TRANSFER_ACCEPT_WORDS} checked={ticked} onChange={(event) => setTicked(event.target.checked)} disabled={busy} />
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setRefusing(true)}>
              Refuse
            </Button>
            <Button variant="primary" size="sm" className="w-full sm:w-auto" loading={acceptIt.isPending} disabled={!ticked || busy} onClick={() => acceptIt.mutate()}>
              Accept
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

/** The counter's home section: each approved transfer to it, with the tick and Accept or Refuse. */
export function IncomingTransfersSection({ enabled, tvs }: { enabled: boolean; tvs: boolean }) {
  const own = useIncomingTransfers('own', enabled);
  const other = useIncomingTransfers('tvs', enabled && tvs);
  const rows: Array<{ row: VehicleTransferRow; book: CounterBook }> = [
    ...(own.data?.data ?? []).map((row) => ({ row, book: 'own' as const })),
    ...(tvs ? (other.data?.data ?? []).map((row) => ({ row, book: 'tvs' as const })) : []),
  ];
  // A failed refresh keeps the rows it already had (and any tick on them); the line only stands alone when there is nothing to show.
  if (own.isError && rows.length === 0) {
    return (
      <p className="text-caption text-age-warn">
        Transfers waiting for you could not be loaded.{' '}
        <button type="button" className="font-medium text-accent hover:underline" onClick={() => void own.refetch()}>
          Try again
        </button>
      </p>
    );
  }
  if (rows.length === 0) return null;
  return (
    <Plane
      label={`Transfers for you to accept · ${formatNumber(rows.length)}`}
      note="Approved by the office. Check the vehicle and its accessories, then tick and accept — it becomes yours to sell."
      raised
    >
      <ul role="list" className="flex flex-col divide-y divide-line">
        {rows.map(({ row, book }) => (
          <IncomingCard key={row.id} row={row} book={book} />
        ))}
      </ul>
    </Plane>
  );
}

/**
 * The one-line notice on every other page of a counter's app. The TVS line is
 * only read here as the home left it — never asked for: a counter with no TVS
 * twin would be refused there (critique, 13 Oct 2026), and the home asks only
 * once the counter's TVS stock has answered.
 */
export function IncomingTransferBanner({ enabled }: { enabled: boolean }) {
  const { pathname } = useLocation();
  const own = useIncomingTransfers('own', enabled);
  const other = useIncomingTransfers('tvs', false);
  const count = (own.data?.data.length ?? 0) + (other.data?.data.length ?? 0);
  if (pathname === '/' || count === 0) return null;
  return (
    <Link
      to="/"
      className="flex min-h-tap items-center justify-between gap-3 rounded-control border border-accent/40 bg-accent-soft px-3 py-2 text-label text-accent transition-colors hover:bg-accent-soft/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <span className="inline-flex items-center gap-2">
        <ArrowLeftRight className="h-4 w-4" aria-hidden />
        {count === 1 ? '1 vehicle transfer is waiting for you to accept' : `${formatNumber(count)} vehicle transfers are waiting for you to accept`}
      </span>
      <span className="inline-flex items-center gap-1 text-caption font-medium">
        Accept it <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Link>
  );
}
